'use server'

/**
 * Server Actions for Agent Management
 *
 * Next.js 14+의 Server Actions를 사용한 보안 강화 아키텍처
 *
 * 장점:
 * 1. API 라우트 불필요 - 파일 관리 간소화
 * 2. 자동 CSRF 보호 - Next.js가 자동으로 처리
 * 3. 타입 안전성 - TypeScript가 자동으로 타입 추론
 * 4. Clerk 통합 간편 - auth() 직접 사용 가능
 * 5. 번들 크기 감소 - 서버에서만 실행되는 코드
 *
 * 보안 강화 (2024-12-02):
 * - Zod 스키마 검증 추가
 * - 입력값 sanitization
 *
 * 사용법:
 * ```tsx
 * import { getAgents, createAgent } from '@/app/actions/agents'
 *
 * // 컴포넌트에서 직접 호출
 * const agents = await getAgents()
 * ```
 */

import { auth } from '@clerk/nextjs/server'
import { revalidatePath } from 'next/cache'
import { validate, createAgentSchema, updateAgentSchema, validateId } from '@/lib/validations'

const NEXT_PUBLIC_BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://backend:8500'

/**
 * 사용자의 모든 에이전트 조회
 */
export async function getAgents() {
  try {
    const { userId } = await auth()

    if (!userId) {
      throw new Error('Unauthorized')
    }

    console.log('[Server Action] getAgents 호출 - userId:', userId)
    console.log('[Server Action] NEXT_PUBLIC_BACKEND_URL:', NEXT_PUBLIC_BACKEND_URL)

    const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/agents/`, {
      headers: {
        'Authorization': `Bearer ${userId}`,
        'X-User-Id': userId,
      },
      // Next.js 캐싱 옵션
      cache: 'no-store', // 개발 중에는 캐시 비활성화
    })

    console.log('[Server Action] Response status:', response.status)

    if (!response.ok) {
      const errorText = await response.text()
      console.error('[Server Action] Response error:', errorText)
      throw new Error(`Failed to fetch agents: ${response.status} ${errorText}`)
    }

    const data = await response.json()
    console.log('[Server Action] Agents fetched:', data?.length || 0, 'agents')

    return data
  } catch (error) {
    console.error('[Server Action] getAgents error:', error)
    throw error
  }
}

/**
 * 특정 에이전트 조회
 */
export async function getAgent(agentId: string) {
  const { userId } = await auth()

  if (!userId) {
    throw new Error('Unauthorized')
  }

  const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}`, {
    headers: {
      'Authorization': `Bearer ${userId}`,
      'X-User-Id': userId,
    },
    cache: 'no-store',
  })

  if (!response.ok) {
    throw new Error('Failed to fetch agent')
  }

  return response.json()
}

/**
 * 새 에이전트 생성
 */
export async function createAgent(data: {
  name: string
  description?: string
  type?: string
  config?: any
  avatar_code?: string
  realtime_provider?: 'none' | 'openai' | 'google'
  use_realtime_api?: boolean  // 하위 호환성
}) {
  const { userId } = await auth()

  if (!userId) {
    throw new Error('Unauthorized')
  }

  // 입력 검증 (2024-12-02 추가)
  const validation = validate(createAgentSchema, {
    name: data.name,
    description: data.description,
    avatarCode: data.avatar_code,
  })

  if (!validation.success) {
    throw new Error(validation.error)
  }

  const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/agents/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${userId}`,
      'X-User-Id': userId,
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    throw new Error('Failed to create agent')
  }

  // 캐시 무효화 - 새 데이터 즉시 반영
  revalidatePath('/dashboard')

  return response.json()
}

/**
 * 에이전트 수정
 */
export async function updateAgent(agentId: string, data: any) {
  const { userId } = await auth()

  if (!userId) {
    throw new Error('Unauthorized')
  }

  // ID 검증 (2024-12-02 추가)
  const validatedId = validateId(agentId)
  if (!validatedId) {
    throw new Error('유효하지 않은 에이전트 ID입니다')
  }

  // 입력 검증 (2024-12-02 추가)
  const validation = validate(updateAgentSchema, data)
  if (!validation.success) {
    throw new Error(validation.error)
  }

  const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/agents/${validatedId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${userId}`,
      'X-User-Id': userId,
    },
    body: JSON.stringify(validation.data),
  })

  if (!response.ok) {
    throw new Error('Failed to update agent')
  }

  const result = await response.json()

  // 캐시 무효화
  revalidatePath('/dashboard')
  revalidatePath(`/agent/${validatedId}`)

  return result
}

/**
 * 에이전트 아바타 타입 업데이트
 * @param agentId 에이전트 ID
 * @param avatarType 아바타 타입 (glyx, nalnani, kikiz, bambam)
 */
export async function updateAgentAvatarType(agentId: string, avatarType: string) {
  const { userId } = await auth()

  if (!userId) {
    throw new Error('Unauthorized')
  }

  // ID 검증
  const validatedId = validateId(agentId)
  if (!validatedId) {
    throw new Error('유효하지 않은 에이전트 ID입니다')
  }

  // avatarType 검증 - allowed values: glyx, nalnani, kikiz, bambam, gangwha, bobbi, taekwondo, custom
  const allowedTypes = ['glyx', 'nalnani', 'kikiz', 'bambam', 'gangwha', 'bobbi', 'taekwondo', 'custom']
  if (!allowedTypes.includes(avatarType)) {
    throw new Error('유효하지 않은 아바타 타입입니다')
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${validatedId}/avatar-type?avatar_type=${avatarType}`,
    {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${userId}`,
        'X-User-Id': userId,
      },
    }
  )

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Failed to update avatar type: ${errorText}`)
  }

  // 캐시 무효화
  revalidatePath('/dashboard')
  revalidatePath(`/agents/${validatedId}`)

  return response.json()
}

/**
 * 에이전트 삭제 (휴지통으로 이동)
 */
export async function deleteAgent(agentId: string, permanent: boolean = false) {
  const { userId } = await auth()

  if (!userId) {
    throw new Error('Unauthorized')
  }

  const url = permanent
    ? `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}?permanent=true`
    : `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}`

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${userId}`,
      'X-User-Id': userId,
    },
  })

  if (!response.ok) {
    throw new Error('Failed to delete agent')
  }

  // 캐시 무효화
  revalidatePath('/dashboard')

  return { success: true }
}

/**
 * 휴지통의 에이전트 조회
 */
export async function getTrashedAgents() {
  const { userId } = await auth()

  if (!userId) {
    throw new Error('Unauthorized')
  }

  const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/agents/trash`, {
    headers: {
      'Authorization': `Bearer ${userId}`,
      'X-User-Id': userId,
    },
  })

  if (!response.ok) {
    throw new Error('Failed to fetch trashed agents')
  }

  return response.json()
}

/**
 * 휴지통 비우기 (일괄 영구 삭제)
 */
export async function emptyTrash() {
  const { userId } = await auth()

  if (!userId) {
    throw new Error('Unauthorized')
  }

  const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/agents/empty-trash`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${userId}`,
      'X-User-Id': userId,
    },
  })

  if (!response.ok) {
    throw new Error('Failed to empty trash')
  }

  // 캐시 무효화
  revalidatePath('/dashboard')

  return response.json()
}

/**
 * 에이전트 복원
 */
export async function restoreAgent(agentId: string) {
  const { userId } = await auth()

  if (!userId) {
    throw new Error('Unauthorized')
  }

  const response = await fetch(`${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/restore`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${userId}`,
      'X-User-Id': userId,
    },
  })

  if (!response.ok) {
    throw new Error('Failed to restore agent')
  }

  // 캐시 무효화
  revalidatePath('/dashboard')
  revalidatePath('/trash')

  return response.json()
}


/**
 * URL 분석 및 Q&A 생성
 * 
 * @param agentId - 에이전트 ID
 * @param urls - 분석할 URL 목록
 * @param useLlm - AI 모델 사용 여부 (true: AI 모델 사용, false: 간단 생성)
 * @param modelId - 사용할 AI 모델 ID (예: gpt-4o, claude-3-opus, gemini-pro)
 * @returns 분석 결과 및 생성된 Q&A 정보
 */
export async function analyzeUrls(
  agentId: string,
  urls: string[],
  useLlm: boolean = true,
  autoTranslate: boolean = true,
  targetLanguages?: string[]
) {
  const { userId } = await auth()

  if (!userId) {
    throw new Error('Unauthorized')
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/analyze-urls`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userId}`,
        'X-User-Id': userId,
      },
      body: JSON.stringify({
        urls,
        use_llm: useLlm,
        auto_translate: autoTranslate,
        target_languages: targetLanguages,
      }),
    }
  )

  if (!response.ok) {
    const errorText = await response.text()
    console.error('URL analysis error:', errorText)
    throw new Error('Failed to analyze URLs')
  }

  // 캐시 무효화
  revalidatePath(`/agents/${agentId}`)

  return response.json()
}

/**
 * 파일 업로드 (MarkItDown 사용)
 * 
 * @param agentId - 에이전트 ID
 * @param formData - 업로드할 파일 데이터
 *   - file: 업로드할 파일
 *   - create_qa: Q&A 생성 여부
 *   - model_id: 사용할 AI 모델 ID (선택사항)
 * @returns 업로드 결과 및 생성된 문서/Q&A 정보
 */
export async function uploadFile(
  agentId: string,
  formData: FormData
) {
  const { userId } = await auth()

  if (!userId) {
    throw new Error('Unauthorized')
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/upload`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userId}`,
        'X-User-Id': userId,
      },
      body: formData,
    }
  )

  if (!response.ok) {
    throw new Error('Failed to upload file')
  }

  // 캐시 무효화
  revalidatePath(`/agents/${agentId}`)

  return response.json()
}

// Enable Chat 기능 제거됨 - 모든 데이터가 자동으로 Vector화됨

/**
 * 인트로 페이지 설정 업데이트
 * @param agentId 에이전트 ID
 * @param config 인트로 설정 (intro, theme)
 */
export async function updateAgentIntroConfig(
  agentId: string,
  config: {
    intro?: {
      ko?: { title: string; description: string; greeting: string };
      en?: { title: string; description: string; greeting: string };
      ja?: { title: string; description: string; greeting: string };
      zh?: { title: string; description: string; greeting: string };
    };
    theme?: {
      primary_color: string;
      background_gradient: string;
      text_color: string;
      style: string;
    };
  }
) {
  const { userId } = await auth();

  if (!userId) {
    throw new Error('Unauthorized');
  }

  // ID 검증
  const validatedId = validateId(agentId);
  if (!validatedId) {
    throw new Error('유효하지 않은 에이전트 ID입니다');
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${validatedId}/intro-config`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userId}`,
        'X-User-Id': userId,
      },
      body: JSON.stringify(config),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update intro config: ${errorText}`);
  }

  // 캐시 무효화
  revalidatePath(`/agents/${validatedId}`);

  return response.json();
}

/**
 * 아바타 캐릭터 프롬프트 업데이트 (다국어)
 */
export async function updateAvatarCharacterPrompts(
  agentId: string,
  prompts: {
    prompt_ko: string;
    prompt_en: string;
    prompt_zh: string;
    prompt_ja: string;
  }
) {
  const { userId } = await auth();

  if (!userId) {
    throw new Error('Unauthorized');
  }

  // ID 검증
  validateId(agentId);

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${agentId}/avatar-prompts`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userId}`,
        'X-User-Id': userId,
      },
      body: JSON.stringify(prompts),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update character prompts: ${errorText}`);
  }

  // 캐시 무효화
  revalidatePath(`/agent/${agentId}`);

  return response.json();
}

/**
 * 페이지 전체 설정 업데이트 (확장)
 * @param agentId 에이전트 ID
 * @param config 페이지 설정 (intro, theme, page_style)
 */
export async function updateAgentPageConfig(
  agentId: string,
  config: {
    intro?: {
      ko?: { title: string; description: string; greeting: string };
      en?: { title: string; description: string; greeting: string };
      ja?: { title: string; description: string; greeting: string };
      zh?: { title: string; description: string; greeting: string };
    };
    theme?: {
      primary_color: string;
      background_gradient: string;
      text_color: string;
      style: string;
    };
    page_style?: {
      primary_color?: string;
      background_gradient?: string;
      text_color?: string;
      style?: string;
      gradient_preset_id?: string;
      custom_gradient?: string;
      use_custom_gradient?: boolean;
      background_type?: 'gradient' | 'image';
      background_image_url?: string;
      chat_bubble?: {
        ai_background_color: string;
        ai_text_color: string;
        user_background_color: string;
        user_text_color: string;
      };
      input_control?: {
        blur_intensity: number;
        placeholder_text: string;
        voice_button_text: string;
        chat_button_text: string;
      };
      consent_modal?: {
        title: string;
        button_text: string;
        description: string;
        modal_image_url?: string;
        profile_image_url?: string;
        video_url?: string;
      };
    };
    opengraph?: {
      og_title?: string;
      og_description?: string;
      og_image_url?: string;
      twitter_image_url?: string;
      kakao_image_url?: string;
    };
  }
) {
  const { userId } = await auth();

  if (!userId) {
    throw new Error('Unauthorized');
  }

  // ID 검증
  const validatedId = validateId(agentId);
  if (!validatedId) {
    throw new Error('유효하지 않은 에이전트 ID입니다');
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${validatedId}/page-config`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userId}`,
        'X-User-Id': userId,
      },
      body: JSON.stringify(config),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update page config: ${errorText}`);
  }

  // 캐시 무효화
  revalidatePath(`/agents/${validatedId}`);

  return response.json();
}

/**
 * 동의 모달 이미지 업로드
 */
export async function uploadConsentModalImage(
  agentId: string,
  formData: FormData
) {
  const { userId } = await auth();

  if (!userId) {
    throw new Error('Unauthorized');
  }

  const validatedId = validateId(agentId);
  if (!validatedId) {
    throw new Error('유효하지 않은 에이전트 ID입니다');
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${validatedId}/consent-modal-image`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userId}`,
        'X-User-Id': userId,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`이미지 업로드 실패: ${errorText}`);
  }

  // 캐시 무효화
  revalidatePath(`/agents/${validatedId}`);

  return response.json();
}

/**
 * AI 프로필 이미지 업로드
 */
export async function uploadProfileImage(
  agentId: string,
  formData: FormData
) {
  const { userId } = await auth();

  if (!userId) {
    throw new Error('Unauthorized');
  }

  const validatedId = validateId(agentId);
  if (!validatedId) {
    throw new Error('유효하지 않은 에이전트 ID입니다');
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${validatedId}/profile-image`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userId}`,
        'X-User-Id': userId,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`이미지 업로드 실패: ${errorText}`);
  }

  // 캐시 무효화
  revalidatePath(`/agents/${validatedId}`);

  return response.json();
}

/**
 * 동의 모달 이미지 삭제
 */
export async function deleteConsentModalImage(agentId: string) {
  const { userId } = await auth();

  if (!userId) {
    throw new Error('Unauthorized');
  }

  const validatedId = validateId(agentId);
  if (!validatedId) {
    throw new Error('유효하지 않은 에이전트 ID입니다');
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${validatedId}/consent-modal-image`,
    {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${userId}`,
        'X-User-Id': userId,
      },
    }
  );

  if (!response.ok) {
    throw new Error('이미지 삭제 실패');
  }

  // 캐시 무효화
  revalidatePath(`/agents/${validatedId}`);

  return { success: true };
}

/**
 * 커버 비디오 업로드
 */
export async function uploadConsentVideo(
  agentId: string,
  formData: FormData
) {
  const { userId } = await auth();

  if (!userId) {
    throw new Error('Unauthorized');
  }

  const validatedId = validateId(agentId);
  if (!validatedId) {
    throw new Error('유효하지 않은 에이전트 ID입니다');
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${validatedId}/consent-video`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userId}`,
        'X-User-Id': userId,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`동영상 업로드 실패: ${errorText}`);
  }

  revalidatePath(`/agents/${validatedId}`);

  return response.json();
}

/**
 * 커버 비디오 삭제
 */
export async function deleteConsentVideo(agentId: string) {
  const { userId } = await auth();

  if (!userId) {
    throw new Error('Unauthorized');
  }

  const validatedId = validateId(agentId);
  if (!validatedId) {
    throw new Error('유효하지 않은 에이전트 ID입니다');
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${validatedId}/consent-video`,
    {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${userId}`,
        'X-User-Id': userId,
      },
    }
  );

  if (!response.ok) {
    throw new Error('동영상 삭제 실패');
  }

  revalidatePath(`/agents/${validatedId}`);

  return { success: true };
}

/**
 * AI 프로필 이미지 삭제
 */
export async function deleteProfileImage(agentId: string) {
  const { userId } = await auth();

  if (!userId) {
    throw new Error('Unauthorized');
  }

  const validatedId = validateId(agentId);
  if (!validatedId) {
    throw new Error('유효하지 않은 에이전트 ID입니다');
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${validatedId}/profile-image`,
    {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${userId}`,
        'X-User-Id': userId,
      },
    }
  );

  if (!response.ok) {
    throw new Error('이미지 삭제 실패');
  }

  // 캐시 무효화
  revalidatePath(`/agents/${validatedId}`);

  return { success: true };
}

// ============================================================================
// 공개 URL 슬러그 관리
// ============================================================================

/**
 * 에이전트의 현재 슬러그 조회
 */
export async function getAgentSlug(agentId: string) {
  const { userId } = await auth();

  if (!userId) {
    throw new Error('Unauthorized');
  }

  const validatedId = validateId(agentId);
  if (!validatedId) {
    throw new Error('유효하지 않은 에이전트 ID입니다');
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${validatedId}/slug`,
    {
      headers: {
        'Authorization': `Bearer ${userId}`,
        'X-User-Id': userId,
      },
      cache: 'no-store',
    }
  );

  if (!response.ok) {
    throw new Error('슬러그 조회 실패');
  }

  return response.json();
}

/**
 * 슬러그 사용 가능 여부 확인
 */
export async function checkSlugAvailability(agentId: string, slug: string) {
  const { userId } = await auth();

  if (!userId) {
    throw new Error('Unauthorized');
  }

  const validatedId = validateId(agentId);
  if (!validatedId) {
    throw new Error('유효하지 않은 에이전트 ID입니다');
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${validatedId}/check-slug/${encodeURIComponent(slug)}`,
    {
      headers: {
        'Authorization': `Bearer ${userId}`,
        'X-User-Id': userId,
      },
      cache: 'no-store',
    }
  );

  if (!response.ok) {
    throw new Error('슬러그 확인 실패');
  }

  return response.json();
}

/**
 * 에이전트 슬러그 업데이트
 */
export async function updateAgentSlug(agentId: string, slug: string) {
  const { userId } = await auth();

  if (!userId) {
    throw new Error('Unauthorized');
  }

  const validatedId = validateId(agentId);
  if (!validatedId) {
    throw new Error('유효하지 않은 에이전트 ID입니다');
  }

  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/agents/${validatedId}/slug`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userId}`,
        'X-User-Id': userId,
      },
      body: JSON.stringify({ slug }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || '슬러그 업데이트 실패');
  }

  // 캐시 무효화
  revalidatePath(`/agents/${validatedId}`);

  return response.json();
}

/**
 * 공개 슬러그로 에이전트 조회 (인증 불필요)
 * 박람회/전시회용 공개 페이지에서 사용
 */
export async function getPublicAgentBySlug(slug: string) {
  const response = await fetch(
    `${NEXT_PUBLIC_BACKEND_URL}/api/public/s/${encodeURIComponent(slug)}`,
    {
      cache: 'no-store',
    }
  );

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    throw new Error('에이전트 조회 실패');
  }

  return response.json();
}